import { Injectable, inject } from '@angular/core';
import {
  ApplicationConfig,
  ApplicationConfigStep,
  ApplicationData,
  ApplicationHooks,
  ApplicationHooksData,
  ApplicationStep,
  ApplicationStepType,
  Proposal,
  ProposalPermissionsDef,
  ProposalPrintGroup,
} from '@lockton-apps/shared/interfaces';
import {
  camelCase,
  filter,
  find,
  forEach,
  get,
  includes,
  join,
  kebabCase,
  map,
  reduce,
  remove,
  set,
  some,
  sortBy,
  uniqBy,
} from 'lodash';
import { FormGroup } from '@angular/forms';
import { DynamicFormBuilderService } from '@lockton-apps/dynamic-forms';

export type AddonHookValidationRules = Record<
  string,
  Record<
    string,
    Record<
      string,
      Array<string | { path: string; iteratorFn: (value: unknown) => boolean }>
    >
  >
>;

export type TemplateValidationRules = Record<
  string,
  Record<
    string,
    {
      path: string;
      iteratorFn: (
        value: unknown,
        stepConfig: ApplicationConfigStep
      ) => Array<{
        step: ApplicationConfigStep;
        identifier: string;
        iteratorLabel: string;
      }>;
    }
  >
>;

@Injectable()
export class ProposalsHooksService<TMeta> implements ApplicationHooks<unknown> {
  private dynamicFormBuilder = inject(DynamicFormBuilderService);

  private deactivateDialogContentPara1 = $localize`You have made changes to the proposal form without saving the step.`;
  private deactivateDialogContentPara2 = $localize`If you would like to keep these changes please stay on the page and click
  “Save progress” or “Save & continue” before you proceed.`;

  protected readonly deactivateDialogData = {
    title: $localize`Unsaved changes`,
    content: `<p class="mb-2">
                ${this.deactivateDialogContentPara1}
              </p>
              <p class="mb-2">
                ${this.deactivateDialogContentPara2}
              </p>`,
    confirmButtonText: $localize`Discard changes`,
    cancelButtonText: $localize`Stay on the page`,
  };

  meta!: TMeta;
  data: ApplicationHooksData = {
    deactivateDialog: this.deactivateDialogData,
  };
  addonHookValidationRules: AddonHookValidationRules = {};
  permissionsRegistry: ProposalPermissionsDef[] = [];
  templateValidationRules: TemplateValidationRules = {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  beforeInit(proposal: Proposal, config: ApplicationConfig): Proposal {
    return proposal;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterInit(proposal: Proposal, config: ApplicationConfig): Proposal {
    return proposal;
  }

  beforePrint(
    data: ApplicationData,
    config: ApplicationConfig,
    steps: ApplicationStep[]
  ): ProposalPrintGroup[] {
    const templateValidations = this.templateValidationRules[config.id];

    let configSteps = config.steps || [];
    const templateStepIds: string[] = [];

    if (templateValidations) {
      configSteps = sortBy(
        reduce(
          templateValidations,
          (acc, validation, stepId) => {
            const template = get(config?.templates, stepId);

            if (!template) {
              console.warn('Template not found for step', stepId);
              return acc;
            }

            const innerSteps = validation.iteratorFn(
              get(data, validation.path),
              template
            );

            const mappedSteps = map(
              innerSteps,
              ({ step, identifier, iteratorLabel }) => ({
                ...step,
                id: join([kebabCase(step.id), identifier], '_'),
                label: iteratorLabel,
                indexOverride: ((step.indexOverride || 0) * 10 + 1) / 10,
              })
            );

            // Add step ids so they are included in the print as steps excludes template steps
            mappedSteps.forEach((step) => {
              templateStepIds.push(step.id);
            });

            return [...acc, ...mappedSteps];
          },
          configSteps
        ),
        'indexOverride'
      );
    }

    return reduce(
      filter(configSteps, ({ id }) =>
        some(steps, (step) => step.id === id || includes(templateStepIds, id))
      ),
      (result: ProposalPrintGroup[], config: any, index: number) => {
        result.push({
          id: camelCase(config.id),
          label: config.label,
          fields: this.dynamicFormBuilder.buildFields(config, data, index, {
            print: true,
          }),
        });

        return result;
      },
      []
    );
  }

  protected handleDynamicSteps(
    data: ApplicationData,
    proposal: Proposal,
    config: ApplicationConfig
  ): Proposal {
    forEach(
      this.addonHookValidationRules[config.id],
      (requiredFields, stepId) => {
        const atLeastOneRequiredValue: boolean = some(
          requiredFields['anyOf'],
          (iterator) => {
            if (typeof iterator === 'string') {
              return !!get(data, iterator);
            }
            const value = get(data, iterator.path);
            return iterator.iteratorFn(value);
          }
        );

        if (atLeastOneRequiredValue) {
          proposal.formData.steps.push({ id: kebabCase(stepId), state: 0 });
        } else {
          remove(proposal.formData.steps, { id: kebabCase(stepId) });
          if (proposal.formData.data && proposal.formData.data[stepId]) {
            delete proposal.formData.data[stepId];
          }
        }
      }
    );

    const templateValidations = this.templateValidationRules[config.id];

    if (templateValidations) {
      forEach(templateValidations, (validation, stepId) => {
        const template = get(config?.templates, stepId);

        if (!template) {
          console.warn('Template not found for step', stepId);
          return;
        }

        const steps = validation.iteratorFn(
          get(data, validation.path),
          template
        );

        const mappedSteps = map(
          steps,
          ({ step, identifier, iteratorLabel }) => ({
            id: join([kebabCase(step.id), identifier], '_'),
            state: 0,
            type: ApplicationStepType.Template,
            meta: {
              iteratorLabel,
              indexOverride: ((step.indexOverride || 0) * 10 + 1) / 10,
            },
          })
        );

        const removed = remove(
          proposal.formData.steps,
          (step) =>
            includes(step.id, template.id) &&
            !some(mappedSteps, (newStep) => newStep.id === step.id)
        );

        forEach(removed, (step) => {
          delete proposal.formData.data[camelCase(step.id)];
        });

        proposal.formData.steps.push(...mappedSteps);
      });
    }

    proposal.formData.steps = sortBy(
      uniqBy(proposal.formData.steps, 'id'),
      ({ id, meta }) =>
        find(config.steps, { id })?.indexOverride || meta?.indexOverride || 0
    );

    return proposal;
  }

  protected handlePermissions(proposal: Proposal, form: FormGroup): Proposal {
    forEach(
      this.permissionsRegistry,
      (permissionDef: ProposalPermissionsDef) => {
        if (form.get(permissionDef.source) && proposal.formData.permissions) {
          set(
            proposal.formData.permissions,
            `${permissionDef.type}.${permissionDef.target}.status`,
            permissionDef.validator(form)
          );
        }
      }
    );

    return proposal;
  }
}
